import { RobotCloudAPI, } from '@moderepo/mode-apis';
import { searchParamsToString, } from '@moderepo/mode-data-state-base';
/**
 * The types of action that can be dispatched to update this sub state.
 */
export var RobotCloudActionType;
(function (RobotCloudActionType) {
    RobotCloudActionType["SET_ROBOT_CATALOG"] = "set project robot definitions";
    RobotCloudActionType["SET_PROJECT_SETTINGS"] = "set project settings";
    RobotCloudActionType["SET_ROBOT_STATUS"] = "set robot status";
    RobotCloudActionType["SET_ROBOTS"] = "set robots";
    RobotCloudActionType["SET_ROBOT"] = "set robot";
    RobotCloudActionType["CLEAR_ROBOTS"] = "clear robots";
    RobotCloudActionType["SET_PROJECT_GATEWAY_CATALOG"] = "set project gateway definitions";
})(RobotCloudActionType || (RobotCloudActionType = {}));
export const setRobotCatalog = (projectId, robotCatalog) => {
    return {
        type: RobotCloudActionType.SET_ROBOT_CATALOG,
        projectId,
        robotCatalog,
    };
};
export const setRobotCloudProjectSettings = (projectId, settings) => {
    return {
        type: RobotCloudActionType.SET_PROJECT_SETTINGS,
        projectId,
        settings,
    };
};
export const setRobotStatus = (homeId, robotId, status) => {
    return {
        type: RobotCloudActionType.SET_ROBOT_STATUS,
        homeId,
        robotId,
        status,
    };
};
export const setRobots = (projectId, robots, searchParams) => {
    return {
        type: RobotCloudActionType.SET_ROBOTS,
        projectId,
        robots,
        searchParams,
    };
};
export const setRobot = (projectId, robot) => {
    return {
        type: RobotCloudActionType.SET_ROBOT,
        projectId,
        robot,
    };
};
export const clearRobots = (projectId) => {
    return {
        type: RobotCloudActionType.CLEAR_ROBOTS,
        projectId,
    };
};
export const setProjectGatewayCatalog = (projectId, gatewayCatalog) => {
    return {
        type: RobotCloudActionType.SET_PROJECT_GATEWAY_CATALOG,
        projectId,
        gatewayCatalog,
    };
};
/**
 * Fetch Robot Definitions for the whole project
 * @param projectId
 */
export const fetchRobotCatalog = (projectId) => {
    return async (dataDispatch) => {
        try {
            const robotDef = await RobotCloudAPI.getInstance().getRobotCatalog(projectId);
            // Robot Definition loaded successfully, dispatch an action to add them to the state
            dataDispatch(setRobotCatalog(projectId, robotDef));
        }
        catch (error) {
            // Robot Definition failed to load. Robot Catalog is OPTIONAL so if it failed to load, we will treat it as having NO robot definition
            // instead of throwing an error. Therefore, if getRobotCatalog call failed, we will call setRobotCatalog
            // and give it an empty array and return the array.
            dataDispatch(setRobotCatalog(projectId, []));
        }
    };
};
/**
 * @param projectId - The id of the project the project settings
 */
export const fetchRobotCloudProjectSettings = (projectId) => {
    return async (dataDispatch) => {
        const settings = await RobotCloudAPI.getInstance().getProjectSettings(projectId);
        dataDispatch(setRobotCloudProjectSettings(projectId, settings));
    };
};
/**
 * @param projectId - The id of the project the project settings
 */
export const updateRobotCloudProjectSettings = (projectId, settings) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateProjectSettings(projectId, settings);
        await dataDispatch(fetchRobotCloudProjectSettings(projectId));
    };
};
/**
 * Fetch status for 1 robot
 * @param homeId - The id of the project the robot belonged to
 * @param robotId - The id of the robot we want to fetch status for
 */
export const fetchRobotStatus = (homeId, robotId) => {
    return async (dataDispatch) => {
        const status = await RobotCloudAPI.getInstance().getRobotStatus(homeId, robotId);
        await dataDispatch(setRobotStatus(status.homeId, robotId, status));
    };
};
/**
 * Fetch status for multiple robots
 * @param homeId - The id of the project the robot belonged to
 * @param robotIds - The ids of the robots we want to fetch status for
 */
export const fetchRobotsStatuses = (homeId, robotIds) => {
    return async (dataDispatch) => {
        const statuses = await RobotCloudAPI.getInstance().getRobotStatusesByIds(homeId, robotIds);
        statuses.forEach((status) => {
            dataDispatch(setRobotStatus(status.homeId, status.robotId, status));
        });
    };
};
/**
 * Get all robots for a project with the given filters
 * @param projectId - The id of the project
 */
export const fetchRobots = (projectId, filters) => {
    return async (dataDispatch) => {
        var _a;
        const modifiedFilters = {
            ...filters,
            skip: filters.pageNumber * filters.pageSize,
            limit: (_a = filters.pageSize) !== null && _a !== void 0 ? _a : 0,
            pageNumber: undefined,
            pageSize: undefined,
        };
        const robots = await RobotCloudAPI.getInstance().getRobots(projectId, modifiedFilters);
        await dataDispatch(setRobots(projectId, robots, searchParamsToString(filters)));
    };
};
/**
 * Get 1 robot
 * @param projectId - The id of the project
 * @param robotId - The id of the robot
 */
export const fetchRobotById = (projectId, homeId, robotId) => {
    return async (dataDispatch) => {
        const robot = await RobotCloudAPI.getInstance().getRobotById(homeId, robotId);
        await dataDispatch(setRobot(projectId, robot));
    };
};
/**
 * Create a robot
 * @param projectId - The id of the project
 * @param robotId - The id of the robot
 */
export const createRobot = (projectId, homeId, data) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().createRobot(homeId, data);
        await dataDispatch(clearRobots(projectId));
    };
};
/**
 * Update a robot
 * @param projectId - The id of the project
 * @param robotId - The id of the robot
 */
export const updateRobot = (projectId, homeId, robotId, data) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().updateRobot(homeId, robotId, data);
        if (data.deviceId !== undefined) {
            // if deviceId is a number or NULL, that means it changed. We need to clear the robots list and reload new one because the list is
            // no longer valid.
            await dataDispatch(clearRobots(projectId));
        }
        else {
            // refetch robot info
            await dataDispatch(fetchRobotById(projectId, homeId, robotId));
        }
    };
};
/**
 * Get 1 robot
 * @param projectId - The id of the project
 * @param robotId - The id of the robot
 */
export const deleteRobot = (projectId, homeId, robotId) => {
    return async (dataDispatch) => {
        await RobotCloudAPI.getInstance().deleteRobot(homeId, robotId);
        await dataDispatch(clearRobots(projectId));
    };
};
/**
 * Fetch Gate Definitions for the whole project
 * @param projectId
 */
export const fetchRCProjectGatewayCatalog = (projectId) => {
    return async (dataDispatch) => {
        try {
            const gatewayDef = await RobotCloudAPI.getInstance().getGatewayCatalog(projectId);
            // Gateway Definition loaded successfully, dispatch an action to add them to the state
            await dataDispatch(setProjectGatewayCatalog(projectId, gatewayDef));
        }
        catch (error) {
            // Gateway Definition failed to load. GatewayDefinition is OPTIONAL so if it failed to load, we will treat it as having NO gateway
            // definition instead of throwing an error. Therefore, if getAllGatewayCatalog call failed, we will call setProjectGatewayCatalog
            // and give it an empty array and return the array.
            await dataDispatch(setProjectGatewayCatalog(projectId, []));
        }
    };
};
